import React, {
	createContext,
	useContext,
	useState,
	useRef,
	useCallback,
	useMemo,
	useEffect,
} from "react";
import apiV2 from "api-v2";
import { CancelFileUploadRoute } from "helpers/api/apiRoutes";
import { ApiResponse } from "types/common.types";
import { FileableFile } from "types/storage.types";
import { UploadedFile } from "../types/UploadedFile.type";
import { ChunkUploadExtraData, UploadState } from "../types/ChunkUpload.type";
import { convertFileAbleFileToUploadedFile } from "../utils";
import { logIfDebug } from "utils/logging";

interface FileUploadContextValue {
	uploadFile: (
		uploadFile: UploadedFile,
		extraData?: ChunkUploadExtraData,
		onComplete?: (file: UploadedFile) => void
	) => Promise<void>;
	cancelAllUploads: () => void;
	cancelUpload: (uploadId: string) => void;
	getUploadState: (uploadId: string) => UploadState;
	clearUploadState: (uploadId: string) => void;
	isUploading: boolean;
}

const FileUploadContext = createContext<FileUploadContextValue | null>(null);

interface FileUploadProviderProps {
	children: React.ReactNode;
	url?: string;
	chunkSize?: number;
	maxRetries?: number;

	/**
	 * How long to keep completed/cancelled state before cleanup (ms)
	 */
	stateCleanupDelayMs?: number;

	/**
	 * Periodically clean up old state for uploads that were never completed or cleaned up (default true)
	 */
	enableAggressiveCleanup?: boolean;

	/**
	 * How often to run cleanup (default: 60000ms / 1 minute)
	 */
	cleanupIntervalMs?: number;
}

export const FileUploadProvider: React.FC<FileUploadProviderProps> = ({
	children,
	url = "/file-upload/store",
	chunkSize = 1024 * 1024,
	maxRetries = 3,
	stateCleanupDelayMs = 2000,
	enableAggressiveCleanup = true,
	cleanupIntervalMs = 60000,
}) => {
	const [uploadStates, setUploadStates] = useState<Map<string, UploadState>>(
		new Map()
	);
	const controllersRef = useRef<Map<string, AbortController>>(new Map());
	const callbacksRef = useRef<Map<string, (file: UploadedFile) => void>>(
		new Map()
	);
	const timestampsRef = useRef<Map<string, number>>(new Map());

	const isUploading = useMemo(() => {
		return Array.from(uploadStates.values()).some((state) => state.isUploading);
	}, [uploadStates]);

	useEffect(() => {
		if (!enableAggressiveCleanup) {
			return;
		}

		const interval = setInterval(() => {
			const now = Date.now();
			const staleThreshold = 5 * 60 * 1000;

			setUploadStates((prev) => {
				const newMap = new Map(prev);
				const toDelete: string[] = [];

				newMap.forEach((state, uploadId) => {
					const timestamp = timestampsRef.current.get(uploadId) || now;
					const age = now - timestamp;

					if (!state.isUploading && age > staleThreshold) {
						toDelete.push(uploadId);
					}
				});

				toDelete.forEach((id) => {
					newMap.delete(id);
					timestampsRef.current.delete(id);
					controllersRef.current.delete(id);
					callbacksRef.current.delete(id);
				});

				if (toDelete.length > 0) {
					logIfDebug(
						`[FileUpload] Cleaned up ${toDelete.length} stale upload states`
					);
				}

				return newMap;
			});
		}, cleanupIntervalMs);

		return () => clearInterval(interval);
	}, [enableAggressiveCleanup, cleanupIntervalMs]);

	const getUploadState = useCallback(
		(uploadId: string): UploadState => {
			return (
				uploadStates.get(uploadId) || {
					progress: 0,
					isUploading: false,
					isUploadCompleted: false,
					error: null,
				}
			);
		},
		[uploadStates]
	);

	const updateUploadState = useCallback(
		(uploadId: string, updates: Partial<UploadState>) => {
			setUploadStates((prev) => {
				const newMap = new Map(prev);
				const currentState = prev.get(uploadId) || {
					progress: 0,
					isUploading: false,
					isUploadCompleted: false,
					error: null,
				};
				newMap.set(uploadId, { ...currentState, ...updates });
				timestampsRef.current.set(uploadId, Date.now());
				return newMap;
			});
		},
		[]
	);

	const clearUploadState = useCallback((uploadId: string) => {
		setUploadStates((prev) => {
			const newMap = new Map(prev);
			newMap.delete(uploadId);
			return newMap;
		});
		controllersRef.current.delete(uploadId);
		callbacksRef.current.delete(uploadId);
		timestampsRef.current.delete(uploadId);
	}, []);

	const cancelUploadOnServer = useCallback(async (uploadId: string) => {
		try {
			await apiV2.post(CancelFileUploadRoute, { uploadId });
		} catch {
			// best-effort cancellation
		}
	}, []);

	const cancelUpload = useCallback(
		(uploadId: string) => {
			const controller = controllersRef.current.get(uploadId);

			if (controller) {
				controller.abort();
				updateUploadState(uploadId, {
					isUploading: false,
					error: new Error("Upload cancelled by user"),
				});

				setTimeout(() => {
					clearUploadState(uploadId);
				}, 1000);
			}
		},
		[updateUploadState, clearUploadState]
	);

	const cancelAllUploads = useCallback(() => {
		const activeUploadIds = Array.from(controllersRef.current.keys());

		if (activeUploadIds.length === 0) {
			return;
		}

		logIfDebug(
			`[FileUpload] Cancelling ${activeUploadIds.length} active uploads`
		);

		activeUploadIds.forEach((uploadId) => {
			const controller = controllersRef.current.get(uploadId);
			if (controller) {
				controller.abort();
			}
		});

		setUploadStates((prev) => {
			const newMap = new Map(prev);
			activeUploadIds.forEach((uploadId) => {
				const currentState = prev.get(uploadId);
				if (currentState) {
					newMap.set(uploadId, {
						...currentState,
						isUploading: false,
						error: new Error("Upload cancelled - page closing"),
					});
					timestampsRef.current.set(uploadId, Date.now());
				}
			});
			return newMap;
		});

		setTimeout(() => {
			activeUploadIds.forEach((uploadId) => clearUploadState(uploadId));
		}, 1000);
	}, [clearUploadState]);

	const uploadFile = useCallback(
		async (
			uploadFile: UploadedFile,
			extraData: ChunkUploadExtraData = {},
			onComplete?: (file: UploadedFile) => void
		) => {
			const file = uploadFile.raw;
			const uploadId = uploadFile.uploadId;
			const totalChunks = Math.ceil(file.size / chunkSize);

			if (onComplete) {
				callbacksRef.current.set(uploadId, onComplete);
			}

			controllersRef.current.set(uploadId, new AbortController());

			updateUploadState(uploadId, {
				progress: 0,
				error: null,
				isUploading: true,
				isUploadCompleted: false,
			});

			let finalUploadedFile: UploadedFile = null;

			const uploadChunk = async (
				formData: FormData,
				chunkIndex: number,
				retries = 0
			): Promise<ApiResponse<FileableFile>> => {
				const controller = controllersRef.current.get(uploadId);

				try {
					const result = await apiV2.post<ApiResponse<FileableFile>>(
						url,
						formData,
						{
							headers: { "Content-Type": "multipart/form-data" },
							signal: controller?.signal,
						}
					);

					const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
					updateUploadState(uploadId, { progress });

					return result.data;
				} catch (err: any) {
					if (err?.code === "ERR_CANCELED") {
						controllersRef.current.delete(uploadId);
						await cancelUploadOnServer(uploadId);
						throw err;
					}

					if (retries < maxRetries) {
						return uploadChunk(formData, chunkIndex, retries + 1);
					}

					controllersRef.current.delete(uploadId);
					throw err;
				}
			};

			try {
				for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
					const start = chunkIndex * chunkSize;
					const end = Math.min(start + chunkSize, file.size);
					const chunk = file.slice(start, end);

					const formData = new FormData();
					formData.append("file", chunk);
					formData.append("resumableChunkNumber", String(chunkIndex));
					formData.append("resumableTotalChunks", String(totalChunks));
					formData.append("originalName", file.name);
					formData.append("uploadId", uploadId);

					Object.entries(extraData).forEach(([key, value]) => {
						formData.append(key, String(value));
					});

					const uploadResult = await uploadChunk(formData, chunkIndex);

					if (chunkIndex + 1 === totalChunks) {
						finalUploadedFile = convertFileAbleFileToUploadedFile(
							uploadResult?.data
						);
					}
				}

				updateUploadState(uploadId, {
					isUploading: false,
					isUploadCompleted: true,
				});

				const callback = callbacksRef.current.get(uploadId);
				if (callback && finalUploadedFile) {
					callback(finalUploadedFile);
				}

				setTimeout(() => {
					clearUploadState(uploadId);
				}, stateCleanupDelayMs);
			} catch (err) {
				updateUploadState(uploadId, {
					isUploading: false,
					error: err instanceof Error ? err : new Error("Upload failed"),
				});
			}
		},
		[
			chunkSize,
			maxRetries,
			url,
			stateCleanupDelayMs,
			updateUploadState,
			clearUploadState,
			cancelUploadOnServer,
		]
	);

	const value: FileUploadContextValue = {
		uploadFile,
		cancelAllUploads,
		cancelUpload,
		getUploadState,
		clearUploadState,
		isUploading,
	};

	return (
		<FileUploadContext.Provider value={value}>
			{children}
		</FileUploadContext.Provider>
	);
};

export const useFileUploadContext = () => {
	const context = useContext(FileUploadContext);
	if (!context) {
		throw new Error(
			"useFileUploadContext must be used within a FileUploadProvider. " +
				"Wrap your app or form with <FileUploadProvider>."
		);
	}
	return context;
};

export const useHasFileUploadContext = () => {
	return useContext(FileUploadContext) !== null;
};
